import { Component, Input, OnDestroy, OnInit, EventEmitter, Output, ChangeDetectorRef } from '@angular/core';
import { NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { UploadStateStyleEnum } from 'app/shared/upload-file/upload-file.enum';
import { UploadFileService } from 'app/shared/upload-file/upload-file.service';
import { FileService } from 'app/shared/file.service';
import { ToastrService } from 'ngx-toastr';
import { saveAs } from 'file-saver';
import { Subscription } from 'rxjs-compat';
import { DynamicLabelManagerSections } from '../users-setting.constant';
import { v4 as uuid } from 'uuid';
import { LabelType } from 'app/API.service';
import * as ExcelJS from 'exceljs';

export interface IUploadResult {
  failedItems: any[];
  cleared: number;
  failed: number;
}

@Component({
  selector: 'cygov-bulk-dynamic-label-upload',
  templateUrl: './bulk-dynamic-label-upload.component.html',
  styleUrls: ['./bulk-dynamic-label-upload.component.scss'],
  standalone: false,
})
export class BulkDynamicLabelUploadComponent implements OnInit, OnDestroy {
  @Output() data = new EventEmitter<any[]>();
  @Output() closePopUp = new EventEmitter<boolean>();
  @Input() dynamicLabelManagerMapper: any;

  uploadStateEnum = UploadStateStyleEnum;
  requiredInfo: { fileType: string } = { fileType: 'DYNAMIC_LABELS_FILE' };
  subscriptions: Subscription[] = [];
  uploadResult: IUploadResult | Record<string, any> = {
    failedItems: [],
    cleared: 0,
    failed: 0,
  };

  nextStepBtnText = 'NEXT';
  disableBtn = false;
  disableBackBtn = false;
  invalidFileData = false;
  closeModalInstance = null;
  uploadedFile: File | null = null;
  categorySearchText = '';
  tagSearchText = '';
  filteredDynamicLabelManagerItems: any = {};

  steps = [
    { num: 'first', title: 'Download Template', active: true, completed: false },
    { num: 'Second', title: 'Upload Template', active: false, completed: false },
    { num: 'Third', title: 'Results', active: false, completed: false },
  ];

  ngbModalOptions: NgbModalOptions = {
    size: 'sm',
    windowClass: 'modal-template-cont dynamic-label-bulk-upload-popup-window',
    backdropClass:
      'modal-template-cont-backdrop dynamic-label-bulk-upload-popup-backdrop-modal md-modal-template-backdrop',
    backdrop: 'static',
  };

  newTagValue: string = '';
  searchFormObject = {
    tagSearchText: '',
    categorySearchText: '',
  };
  isAddEnabled: boolean;
  currentSelectedSection: string;
  selectedDynamicLabelManagerItems: any[] = [];

  constructor(
    private toastrService: ToastrService,
    private uploadService: UploadFileService,
    private cdRef: ChangeDetectorRef,
    private fileService: FileService
  ) {}

  ngOnInit(): void {
    this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
    this.uploadResult.failedItems ||= [];
    this.subscriptions.push(
      this.uploadService.getFile().subscribe(file => {
        if (!file || Array.isArray(file)) {
          return;
        }
        this.uploadedFile = file;
        // this.uploadService.setUploadState(UploadStateEnum.UPLOAD_STARTED);
        setTimeout(() => {
          this.parseFileData();
        }, 50);
      })
    );
  }

  moveToNextStep(): Promise<void> {
    const currentStepIndex = this.steps.findIndex(s => s.active);
    switch (currentStepIndex) {
      case 0: {
        this.disableBtn = true;
        break;
      }
      case 1: {
        if (!this.dynamicLabelManagerMapper.categories.length && !this.dynamicLabelManagerMapper.tags.length) {
          this.toastrService.error('Please upload a file before proceeding');
          return;
        }
        this.nextStepBtnText = 'ADD';
        this.disableBtn = false;
        break;
      }

      case 2: {
        this.disableBtn = false;
        this.nextStepBtnText = 'NEXT';
        this.uploadService.resetEverything();
        this.data.emit(this.filteredDynamicLabelManagerItems);
        break;
      }
    }

    if (this.steps[currentStepIndex + 1]) {
      this.steps[currentStepIndex].active = false;
      this.steps[currentStepIndex].completed = true;
      this.steps[currentStepIndex + 1].active = true;
    }
  }

  moveBackToOldStep(): void {
    const currentStepIndex = this.steps.findIndex(s => s.active);

    if (this.steps[currentStepIndex - 1]) {
      this.steps[currentStepIndex].active = false;
      this.steps[currentStepIndex - 1].active = true;
      this.steps[currentStepIndex - 1].completed = false;
    }

    switch (currentStepIndex - 1) {
      case 0: {
        this.disableBtn = false;
        this.nextStepBtnText = 'NEXT';
        this.uploadService.resetEverything();
        break;
      }
      case 1: {
        this.disableBtn = true;
        this.nextStepBtnText = 'NEXT';
        this.uploadService.resetEverything();
        this.invalidFileData = false;
        this.categorySearchText = '';
        this.tagSearchText = '';
        this.dynamicLabelManagerMapper.tags = [];
        this.dynamicLabelManagerMapper.categories = [];
        this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
        break;
      }
    }
  }

  resetPopupState(): void {
    this.steps.forEach((step, index) => {
      step.active = !index;
      step.completed = false;
    });
    this.nextStepBtnText = 'NEXT';
    this.uploadResult = {};
    this.disableBtn = false;
    this.categorySearchText = '';
    this.tagSearchText = '';
  }

  resetAndClosePopup(): void {
    this.resetPopupState();
    this.uploadService.resetEverything();
    if (this.closeModalInstance) {
      this.closeModalInstance.close();
    }
    this.closePopUp.next(true);
  }

  async downloadCSVTemplate(): Promise<void> {
    try {
      let fileName = '';
      switch (this.dynamicLabelManagerMapper.type) {
        case LabelType.GENERAL: {
          fileName = 'General_Tags';
          break;
        }
        case LabelType.RISK: {
          fileName = 'Risk_Tags';
          break;
        }
        case LabelType.POLICY: {
          fileName = 'Policy_Management';
          break;
        }
      }
      const Path = `DYNAMIC_LABEL_TEMPLATES/${fileName}.xlsx`;
      const url = await this.fileService.downloadFileFromS3(Path);
      const response = await fetch(url);
      const blob = await response.blob();
      saveAs(blob, `${fileName}.xlsx`);
      this.toastrService.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template', error);
      this.toastrService.error('Error downloading template');
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions && !!this.subscriptions.length) {
      this.subscriptions.forEach(sub => sub.unsubscribe());
    }
  }

  // ================================================
  // ============== Private Methods =================
  // ================================================

  private parseFileData(): void {
    // Mock file processing - simulate parsing and validation
    this.disableBtn = true;
    if (!this.uploadedFile || Array.isArray(this.uploadedFile)) {
      return;
    }

    const currentDate = new Date();
    // const currentUser = this.userService.currentUser;
    const parsedData = [];
    this.disableBtn = true;
    const jsonData: any[] = [];
    const reader = new FileReader();

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const buffer = e.target?.result as ArrayBuffer;
      const workbook = new ExcelJS.Workbook();

      await workbook.xlsx.load(buffer);

      if (!workbook.worksheets.length) {
        this.toastrService.info('No worksheets found in the uploaded file. Please check the file and try again.');
        return;
      }

      let headerslist: string[] = [];

      // We need to iterate over the list of worksheets and then populate the data accordingly.
      workbook.worksheets.forEach(worksheet => {
        const headerRow = worksheet.getRow(1);
        const headers = (Array.isArray(headerRow.values) ? headerRow.values : [])
          .slice(1)
          .map(h => (typeof h === 'string' ? h.trim() : ''));
        if (worksheet.name !== 'Instructions') {
          headerslist = headerslist.concat(headers);
          worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
            if (rowNumber === 1) {
              return;
            } // skip header
            const rowData: any = {};
            headers.forEach((header, colIndex) => {
              rowData[header] = row.getCell(colIndex + 1).value;
              // Need to check if the row data is of string type or empty.
              if (typeof rowData[header] !== 'string' && typeof rowData[header] !== 'number') {
                rowData[header] = '';
                this.uploadResult.failed++;
                this.uploadResult.failedItems.push(rowData);
              }
            });
            jsonData.push(rowData);
          });
        }
      });

      // We need to check the validation of the column headers in file first.
      if (!this.checkValidationOfFile(jsonData, headerslist)) {
        return;
      }

      const categoriesMapper = {};
      jsonData.forEach(item => {
        if (
          DynamicLabelManagerSections.GENERAL_CATEGORIES in item ||
          DynamicLabelManagerSections.RISK_CATEGORIES in item
        ) {
          this.dynamicLabelManagerMapper.categories.push({
            id: uuid(),
            name:
              item[DynamicLabelManagerSections.GENERAL_CATEGORIES] || item[DynamicLabelManagerSections.RISK_CATEGORIES],
            selected: false,
          });
          this.uploadResult.cleared++;
          if (
            item[DynamicLabelManagerSections.GENERAL_CATEGORIES] === null ||
            item[DynamicLabelManagerSections.RISK_CATEGORIES] === null
          ) {
            this.uploadResult.failed++;
            this.uploadResult.failedItems.push(item);
          }
        }

        if (
          DynamicLabelManagerSections.RISK_TAGS in item ||
          DynamicLabelManagerSections.GENERAL_TAGS in item ||
          DynamicLabelManagerSections.POLICY_TAGS in item
        ) {
          this.dynamicLabelManagerMapper.tags.push({
            id: uuid(),
            name:
              item[DynamicLabelManagerSections.RISK_TAGS] ||
              item[DynamicLabelManagerSections.GENERAL_TAGS] ||
              item[DynamicLabelManagerSections.POLICY_TAGS],
            selected: false,
          });
          this.uploadResult.cleared++;
          if (
            item[DynamicLabelManagerSections.RISK_TAGS] === null ||
            item[DynamicLabelManagerSections.GENERAL_TAGS] === null ||
            item[DynamicLabelManagerSections.POLICY_TAGS] === null
          ) {
            this.uploadResult.failed++;
            this.uploadResult.failedItems.push(item);
          }
        }

        if (
          DynamicLabelManagerSections.POLICY_CATEGORIES in item &&
          !categoriesMapper[item[DynamicLabelManagerSections.POLICY_CATEGORIES]]
        ) {
          if (item[DynamicLabelManagerSections.POLICY_CATEGORIES] === null) {
            this.uploadResult.failed++;
            this.uploadResult.failedItems.push(item);
          } else {
            this.uploadResult.cleared++;
          }
          categoriesMapper[item[DynamicLabelManagerSections.POLICY_CATEGORIES]] = {
            values: [],
            name: item[DynamicLabelManagerSections.POLICY_CATEGORIES],
            id: uuid(),
            selected: false,
          };
        }
      });

      jsonData.forEach(item => {
        if (
          DynamicLabelManagerSections.POLICY_SUB_CATEGORIES in item &&
          categoriesMapper[item[DynamicLabelManagerSections.POLICY_CATEGORIES]]
        ) {
          if (
            item[DynamicLabelManagerSections.POLICY_SUB_CATEGORIES] === null ||
            item[DynamicLabelManagerSections.POLICY_SUB_CATEGORIES] === ''
          ) {
            this.uploadResult.failed++;
            this.uploadResult.failedItems.push(item);
          } else {
            this.uploadResult.cleared++;
          }
          categoriesMapper[item[DynamicLabelManagerSections.POLICY_CATEGORIES]].values.push({
            id: uuid(),
            name: item[DynamicLabelManagerSections.POLICY_SUB_CATEGORIES],
            selected: false,
          });
        }
      });

      Object.keys(categoriesMapper).forEach(categoryName => {
        this.dynamicLabelManagerMapper.categories.push({
          name: categoriesMapper[categoryName].name,
          id: categoriesMapper[categoryName].id,
          values: categoriesMapper[categoryName].values.map(subValue => ({
            id: subValue.id,
            name: subValue.name,
            selected: false,
          })),
        });
      });

      this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
      this.disableBtn = false;
      this.moveToNextStep();
      this.cdRef.detectChanges();
    };

    reader.readAsArrayBuffer(this.uploadedFile);
  }

  checkValidationOfFile(jsonData: any[], headerslist: string[]): boolean {
    if (!jsonData.length) {
      this.disableBtn = false;
      this.nextStepBtnText = 'NEXT';
      this.uploadService.resetEverything();
      this.invalidFileData = false;
      this.toastrService.error('The uploaded file is empty');
      return false;
    }

    const fileKeys = Object.keys(jsonData[0]);
    let requiredColumns: string[] = [];

    switch (this.dynamicLabelManagerMapper.type) {
      case LabelType.POLICY: {
        requiredColumns = ['Policy Tags', 'Policy Categories', 'Policy SubCategories'];
        break;
      }
      case LabelType.RISK: {
        requiredColumns = ['Risk Tags', 'Risk Categories'];
        break;
      }
      case LabelType.GENERAL: {
        requiredColumns = ['General Tags', 'General Categories'];
        break;
      }
    }

    const missingColumns = requiredColumns.filter(column => !headerslist.includes(column));

    if (missingColumns.length > 0) {
      this.disableBtn = false;
      this.nextStepBtnText = 'NEXT';
      this.uploadService.resetEverything();
      this.invalidFileData = false;
      missingColumns.forEach(column => {
        this.toastrService.error(`The uploaded file is missing required column: ${column}`);
      });
      return false;
    } else {
      return true;
    }
  }

  addNewTagOrCategory(title: DynamicLabelManagerSections, subCategoryName?: string, type?: LabelType): void {
    const payload = {};
    const isTag = [
      DynamicLabelManagerSections.GENERAL_TAGS,
      DynamicLabelManagerSections.RISK_TAGS,
      DynamicLabelManagerSections.POLICY_TAGS,
    ].includes(title);
    if (this.newTagValue.trim() === '') {
      this.toastrService.error('Field cannot be empty');
      return;
    }
    if (this.newTagValue.length > 30) {
      this.toastrService.error('Field cannot be more than 30 characters');
      return;
    }
    const newId = uuid();
    if (isTag) {
      this.dynamicLabelManagerMapper.tags.push({
        id: newId,
        name: this.newTagValue,
        selected: false,
      });
      this.filteredDynamicLabelManagerItems.tags = JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper.tags));
    } else {
      let categoryId = null;
      if (title === DynamicLabelManagerSections.POLICY_CATEGORIES) {
        if (subCategoryName) {
          const categoryIndex = this.dynamicLabelManagerMapper.categories.findIndex(
            value => value.name === subCategoryName
          );
          if (categoryIndex < 0) {
            return;
          }
          categoryId = this.dynamicLabelManagerMapper.categories[categoryIndex].id;
          this.dynamicLabelManagerMapper.categories[categoryIndex]?.values.push({
            id: newId,
            name: this.newTagValue,
            selected: false,
          });
        } else {
          this.dynamicLabelManagerMapper.categories.unshift({
            id: newId,
            name: this.newTagValue,
            values: [],
          });
        }
      } else {
        this.dynamicLabelManagerMapper.categories.push({
          id: newId,
          name: this.newTagValue,
          selected: false,
        });
      }
      this.uploadResult.cleared++;
      this.filteredDynamicLabelManagerItems.categories = JSON.parse(
        JSON.stringify(this.dynamicLabelManagerMapper.categories)
      );
    }
    this.isAddEnabled = false;
    this.newTagValue = '';
    this.categorySearchText = '';
    this.tagSearchText = '';
  }

  closeAddBoxForTagOrCategory(title: string): void {
    this.isAddEnabled = false;
    this.newTagValue = '';
  }

  selectDynamicLabelManagerItem(item: any): void {
    item.selected = !item.selected;
    if (item.selected && !this.selectedDynamicLabelManagerItems.includes(item)) {
      this.selectedDynamicLabelManagerItems.push(item);
    } else {
      const index = this.selectedDynamicLabelManagerItems.indexOf(item);
      this.selectedDynamicLabelManagerItems.splice(index, 1);
    }
  }

  showAddBoxForTagOrCategory(title: string, subCategoryId?: string): void {
    this.currentSelectedSection = subCategoryId ? subCategoryId : title;
    this.isAddEnabled = true;
  }

  searchDynamicLabelManagerItems(term: any, title: string, type: LabelType): void {
    if (term) {
      if (title === DynamicLabelManagerSections.POLICY_CATEGORIES) {
        this.filteredDynamicLabelManagerItems.categories = this.dynamicLabelManagerMapper.categories.filter(
          category => {
            return category.values.some(value => value.name && value.name.toLowerCase().includes(term.toLowerCase()));
          }
        );
      } else if (
        title === DynamicLabelManagerSections.RISK_CATEGORIES ||
        title === DynamicLabelManagerSections.GENERAL_CATEGORIES
      ) {
        this.filteredDynamicLabelManagerItems.categories = this.dynamicLabelManagerMapper.categories.filter(
          category => category.name && category.name.toLowerCase().includes(term.toLowerCase())
        );
      } else {
        this.filteredDynamicLabelManagerItems.tags = this.dynamicLabelManagerMapper.tags.filter(
          tag => tag.name && tag.name.toLowerCase().includes(term.toLowerCase())
        );
      }
    } else {
      this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
    }
  }

  clearUploadResults(): void {
    if (this.selectedDynamicLabelManagerItems.length) {
      this.selectedDynamicLabelManagerItems.forEach(item => {
        // First check in tag array.
        const tagIndex = this.dynamicLabelManagerMapper.tags.findIndex(tag => tag.id === item.id);
        if (tagIndex >= 0) {
          this.uploadResult.cleared--;
          this.dynamicLabelManagerMapper.tags.splice(tagIndex, 1);
        }

        // Then check in category array.
        if (this.dynamicLabelManagerMapper.type === LabelType.POLICY) {
          this.dynamicLabelManagerMapper.categories.forEach(category => {
            const categoryIndex = category.values.findIndex(value => value.id === item.id);
            if (categoryIndex >= 0) {
              this.uploadResult.cleared--;
              category.values.splice(categoryIndex, 1);
              if (category.name === '') {
                this.uploadResult.failed--;
              }
            }
          });
        } else {
          const categoryIndex = this.dynamicLabelManagerMapper.categories.findIndex(
            category => category.id === item.id
          );
          if (categoryIndex >= 0) {
            this.uploadResult.cleared--;
            this.dynamicLabelManagerMapper.categories.splice(categoryIndex, 1);
          }
        }
      });
      this.filteredDynamicLabelManagerItems = { ...JSON.parse(JSON.stringify(this.dynamicLabelManagerMapper)) };
      this.selectedDynamicLabelManagerItems = [];
    }
  }
}
